// InvoiceClientParty.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Hooks
import useSubscribedNode from 'hooks/useSubscribedNode';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';

// Utilities
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { getNodelistNodeTitle, getNodelistPrimaryKey } from 'utilities/NodelistField';
import { readInvoiceField } from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import {
  getPurchaseOrderSchemaField,
  getShipToPartyDisplay,
} from 'nodetypes/purchaseorder/view/purchaseOrderPartyUtils';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo.css';

// Components
import SelectInput from 'components/selectinput/SelectInput';
import DocumentPartyDetails from 'nodetypes/documents/kit/DocumentPartyDetails';

const CONTACT_TYPE_KEY = 'CONTACT';

/**
 * InvoiceClientParty
 *
 * Bill To contact picker for Invoice General Info.
 */
const InvoiceClientParty = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
}) => {
  const { activeBase } = useBase();

  const baseKey = node?.baseKey || activeBase?.key || null;
  const typeKey = String(nodeType?.key || node?.type || 'INVOICE').trim().toUpperCase();
  const clientField = useMemo(
    () => getPurchaseOrderSchemaField(nodeType, 'client'),
    [nodeType],
  );
  const targetType = String(clientField?.targetType || CONTACT_TYPE_KEY).trim() || CONTACT_TYPE_KEY;
  const selectedKey = getNodelistPrimaryKey(readInvoiceField(node, 'client', nodeType)) || '';
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);

  const { options: contactOptions } = useNodelistTargetOptions({
    targetType,
    baseKey,
    enabled: Boolean(canEdit && baseKey && targetType),
  });

  const selectedContact = useSubscribedNode(selectedKey, {
    enabled: Boolean(selectedKey),
  });

  const display = useMemo(
    () => getShipToPartyDisplay(selectedContact),
    [selectedContact],
  );

  const selectOptions = useMemo(() => {
    const empty = { value: '', label: 'Select a contact…' };
    const byValue = new Map(
      (Array.isArray(contactOptions) ? contactOptions : []).map((option) => [
        String(option.value || '').trim(),
        option,
      ]),
    );

    if (selectedKey && !byValue.has(selectedKey)) {
      byValue.set(selectedKey, {
        value: selectedKey,
        label: selectedContact
          ? getNodelistNodeTitle(selectedContact)
          : 'Unavailable contact',
      });
    }

    return [empty, ...byValue.values()];
  }, [contactOptions, selectedContact, selectedKey]);

  const handleClientChange = useCallback((event) => {
    if (!canEdit) return;
    const nextKey = String(event?.target?.value ?? event ?? '').trim();
    const field = clientField
      ? { ...clientField, allowMultiple: false }
      : {
        name: 'client',
        source: typeKey.toLowerCase(),
        type: 'relation',
        targetType,
        allowMultiple: false,
      };
    const patch = buildNodeFieldPatch(
      node,
      field,
      typeKey,
      nextKey ? [nextKey] : [],
    );
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, clientField, node, onNodeChange, targetType, typeKey]);

  return (
    <div className="po-general-info-party">
      <div className="po-general-info-label">Bill To</div>

      {canEdit ? (
        <div className="po-general-info-select">
          <SelectInput
            value={selectedKey}
            options={selectOptions}
            placeholder="Select a contact…"
            showIcons={false}
            width="100%"
            hybrid
            style={{
              fontWeight: 500,
              height: '32px',
            }}
            onChange={handleClientChange}
          />
        </div>
      ) : (
        <div className={`po-general-info-value${display.title ? '' : ' is-empty'}`}>
          {display.title || '—'}
        </div>
      )}

      <DocumentPartyDetails
        relatedNode={selectedContact}
        address={display.address}
        phone={display.phone}
        email={display.email}
        editLabel="Edit contact"
      />
    </div>
  );
});

InvoiceClientParty.displayName = 'InvoiceClientParty';

export default InvoiceClientParty;
